<template>
  <nav class="navbar" :class="{ 'navbar--open': isMenuOpen, 'navbar--scrolled': isScrolled }">
    <router-link to="/" class="navbar__brand" :aria-label="$t('nav.homeLink')" @click="closeMenu">
      <img :src="logoWordmark" alt="Mirai Motion" class="navbar__logo" />
    </router-link>

    <button
      ref="toggleButton"
      class="navbar__toggle"
      type="button"
      @click="toggleMenu"
      :aria-label="$t('nav.menu')"
      aria-controls="site-menu"
      :aria-expanded="isMenuOpen"
    >
      <span class="navbar__toggle-icon"></span>
      <span class="navbar__toggle-icon"></span>
      <span class="navbar__toggle-icon"></span>
    </button>

    <div id="site-menu" class="navbar__menu" :class="{ 'navbar__menu--open': isMenuOpen }">
      <ul class="navbar__links">
        <li><router-link to="/" @click="closeMenu">{{ $t('nav.home') }}</router-link></li>
        <li><router-link to="/events" @click="closeMenu">{{ $t('nav.events') }}</router-link></li>
        <li><router-link to="/contact" @click="closeMenu">{{ $t('nav.contact') }}</router-link></li>
      </ul>

      <div class="navbar__actions">
        <ThemeAndLanguageSwitcher />
        <a
          :href="ticketUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="btn btn-neon navbar__tickets"
          @click="closeMenu"
        >
          {{ $t('nav.tickets') }}
          <span aria-hidden="true">→</span>
        </a>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import ThemeAndLanguageSwitcher from "@/components/ThemeAndLanguageSwitcher.vue";
import { TICKET_URL } from '@/config/site.config.js';
import logoWordmark from '@/assets/logo2.png';

const ticketUrl = TICKET_URL;
const isMenuOpen = ref(false);
const isScrolled = ref(false);
const toggleButton = ref(null);

const toggleMenu = () => { isMenuOpen.value = !isMenuOpen.value; };
const closeMenu = () => { isMenuOpen.value = false; };

const handleResize = () => {
  if (window.innerWidth >= 900) isMenuOpen.value = false;
};
const handleScroll = () => { isScrolled.value = window.scrollY > 16; };
// Escape sluit het open menu en zet de focus terug op de menuknop, zodat
// toetsenbordgebruikers niet ergens in een verdwenen lijst blijven hangen.
const handleKeydown = (e) => {
  if (e.key !== 'Escape' || !isMenuOpen.value) return;
  isMenuOpen.value = false;
  toggleButton.value?.focus();
};

onMounted(() => {
  window.addEventListener('resize', handleResize);
  window.addEventListener('scroll', handleScroll, { passive: true });
  window.addEventListener('keydown', handleKeydown);
  handleScroll();
});
onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('scroll', handleScroll);
  window.removeEventListener('keydown', handleKeydown);
});
</script>

<style scoped>
.navbar {
  position: sticky;
  top: 0;
  z-index: 1000;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.5rem;
  padding: 0.7rem clamp(1rem, 4vw, 2.5rem);
  border-bottom: 3px solid transparent;
  transition: background 0.3s ease, border-color 0.3s ease, backdrop-filter 0.3s ease;
}
.navbar--scrolled {
  background: rgba(251, 247, 236, 0.88);
  border-bottom-color: var(--ink);
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
}
body.dark-mode .navbar--scrolled { background: rgba(26, 0, 51, 0.9); }

/* Brand logo (wordmark) sits on its own cream plaque so it reads
   cleanly on any background and in both themes */
.navbar__brand {
  display: inline-flex;
  align-items: center;
  transition: transform 0.15s ease;
}
.navbar__brand:hover { text-decoration: none; transform: scale(1.04); }
.navbar__logo { height: 44px; width: auto; display: block; }

/* Toggle */
.navbar__toggle {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 46px;
  height: 46px;
  background: var(--card);
  border: 2.5px solid var(--ink);
  border-radius: 11px;
  box-shadow: 3px 3px 0 var(--ink);
  cursor: pointer;
  padding: 0 11px;
  z-index: 1001;
}
.navbar__toggle-icon {
  width: 100%;
  height: 2.5px;
  background-color: var(--ink);
  border-radius: 3px;
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.navbar--open .navbar__toggle-icon:nth-child(1) { transform: translateY(7.5px) rotate(45deg); }
.navbar--open .navbar__toggle-icon:nth-child(2) { opacity: 0; }
.navbar--open .navbar__toggle-icon:nth-child(3) { transform: translateY(-7.5px) rotate(-45deg); }

/* Menu */
.navbar__menu {
  display: flex;
  align-items: center;
  gap: 2rem;
}
.navbar__links {
  list-style: none;
  display: flex;
  gap: 1.8rem;
  margin: 0;
  padding: 0;
}
.navbar__links a {
  position: relative;
  font-family: var(--font-display);
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--body);
  padding: 0.4rem 0;
  transition: color 0.2s ease;
}
.navbar__links a::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: -1px;
  width: 100%;
  height: 3px;
  background: var(--color-secondary);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.2s ease-out;
}
.navbar__links a:hover,
.navbar__links a.router-link-active {
  color: var(--color-primary);
  text-decoration: none;
}
.navbar__links a:hover::after,
.navbar__links a.router-link-active::after { transform: scaleX(1); }

.navbar__actions {
  display: flex;
  align-items: center;
  gap: 1.1rem;
}
.navbar__tickets {
  padding: 0.6rem 1.15rem;
  min-height: 44px;
  font-size: 0.8rem;
  box-shadow: 4px 4px 0 var(--ink);
}

/* Mobile */
@media (max-width: 899px) {
  .navbar {
    background: rgba(251, 247, 236, 0.92);
    border-bottom-color: var(--ink);
    -webkit-backdrop-filter: blur(10px);
    backdrop-filter: blur(10px);
  }
  body.dark-mode .navbar { background: rgba(26, 0, 51, 0.94); }

  .navbar__toggle { display: flex; }

  .navbar__menu {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    flex-direction: column;
    align-items: stretch;
    gap: 0;
    background: var(--paper);
    border-bottom: 3px solid var(--ink);
    padding: 0.5rem 1.25rem 1.5rem;
    /* Dicht is het menu echt weg, ook voor Tab en schermlezers, en niet
       alleen dichtgeschoven. visibility schakelt pas om na het uitfaden. */
    visibility: hidden;
    opacity: 0;
    transform: translateY(-12px);
    transition: opacity 0.18s ease-in, transform 0.18s ease-in, visibility 0s linear 0.18s;
  }
  .navbar__menu--open {
    visibility: visible;
    opacity: 1;
    transform: none;
    transition: opacity 0.22s ease-out, transform 0.22s ease-out, visibility 0s;
  }
  .navbar__links {
    flex-direction: column;
    gap: 0;
    width: 100%;
  }
  .navbar__links li { border-bottom: 1px solid var(--line); }
  .navbar__links a { display: block; padding: 1rem 0.25rem; font-size: 0.95rem; }
  .navbar__actions {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
    margin-top: 1.25rem;
  }
  .navbar__tickets { width: 100%; }
}

/* Het menu, het streepje onder de links en het hamburgericoon bewegen niet
   bij reduced motion; openen en sluiten gebeurt dan in een keer. */
@media (prefers-reduced-motion: reduce) {
  .navbar,
  .navbar__brand,
  .navbar__toggle-icon,
  .navbar__links a,
  .navbar__links a::after,
  .navbar__menu,
  .navbar__menu--open {
    transition: none;
  }
}
</style>
